import { Stack, useMantineTheme } from '@mantine/core';
import type { Row } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Text } from '@feathr/components';
import { moment, TimeFormat } from '@feathr/hooks';

import BlankRowsLine from './BlankRowsLine';
import DownloadErrorsLine from './DownloadErrorsLine';
import FailureReasonLine from './FailureReasonLine';
import ImportedRowsLine from './ImportedRowsLine';
import { hasRunDetails, hasRunFinished, hasRunStarted } from './ImportRunDetails.utils';
import InitiatedByLine from './InitiatedByLine';
import NothingImportedLine from './NothingImportedLine';
import NotStartedLine from './NotStartedLine';
import PartialImportLine from './PartialImportLine';
import RejectionBreakdown from './RejectionBreakdown';
import SecondRecordsLine from './SecondRecordsLine';

interface IImportRunDetailsProps {
  importer: Importer;
}

/**
 * The Import details panel under an expanded imports-table row. Each line decides for itself
 * whether it applies. The start time is in the browser's timezone, as in the table's date columns.
 */
function ImportRunDetails({ importer }: Readonly<IImportRunDetailsProps>): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  if (!hasRunDetails(importer)) {
    return null;
  }

  // A draft, a queued import, or one that failed before it started has no Initiated-by line.
  const started = hasRunStarted(importer)
    ? moment.utc(importer.get('date_started'), moment.ISO_8601).tz(moment.tz.guess())
    : undefined;

  return (
    <Stack align={'flex-start'} data-name={'import-run-details'} gap={theme.spacing['1']}>
      {/* Placeholder copy, pending Product and Design (decision 23). */}
      <Text.DefaultSemibold>{t('Import details')}</Text.DefaultSemibold>
      <NotStartedLine importer={importer} />
      {started && (
        <InitiatedByLine
          date={started.format(TimeFormat.shortDate)}
          time={started.format(TimeFormat.timeAndTimeZone)}
          userId={importer.get('created_by')}
        />
      )}
      <FailureReasonLine importer={importer} />
      <PartialImportLine importer={importer} />
      <ImportedRowsLine importer={importer} />
      <NothingImportedLine importer={importer} />
      <RejectionBreakdown importer={importer} />
      <SecondRecordsLine importer={importer} />
      <BlankRowsLine importer={importer} />
      {hasRunFinished(importer) && importer.get('errors_key') && (
        <DownloadErrorsLine href={importer.downloadErrorUrl} />
      )}
    </Stack>
  );
}

const ObservedImportRunDetails = observer(ImportRunDetails);

export default ObservedImportRunDetails;

/**
 * Adapts the panel to `TanStackTable`'s `renderSubComponent`, which hands over the row rather
 * than the model. Module scope keeps its identity stable across renders.
 */
export function renderImportRunDetails(row: Row<Importer>): JSX.Element {
  return <ObservedImportRunDetails importer={row.original} />;
}
